import { captureException, captureMessage } from '@sentry/react';
import { Duration } from 'luxon';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The configuration options.
 */
export interface GeolocatedConfig {
  /**
   * The implementer of the Geolocation API.
   * @default navigator.geolocation
   */
  geolocationProvider?: Geolocation;
  /**
   * Allows to set the default value of isGeolocationEnabled.
   * @default true
   */
  isOptimisticGeolocationEnabled?: boolean;
  /**
   * Callback to call when geolocation API invocation fails. Called with undefined when the user decision times out.
   */
  onError?: (positionError?: GeolocationPositionError) => void;
  /**
   * Callback to call when geolocation API invocation succeeds.
   */
  onSuccess?: (position: GeolocationPosition) => void;
  /**
   * The Geolocation API's positionOptions configuration object.
   */
  positionOptions?: PositionOptions;
  /**
   * Time we give to the user to allow the use of Geolocation API before presuming they denied it.
   * @default undefined
   */
  userDecisionTimeout?: number;
  /**
   * If set to true, the component watches for location permission changes.
   * @default false
   */
  watchLocationPermissionChange?: boolean;
}

/**
 * Result of the hook.
 */
export interface GeolocatedResult {
  /**
   * The Geolocation API's coords object containing latitude, longitude, and accuracy and also optionally containing altitude, altitudeAccuracy, heading and speed.
   */
  coords: GeolocationCoordinates | undefined;
  /**
   * Callback you can use to manually trigger the position query.
   */
  getPosition: () => Promise<GeolocationPosition>;
  /**
   * Flag indicating that the browser supports the Geolocation API.
   */
  isGeolocationAvailable: boolean;
  /**
   * Flag indicating that the user has allowed the use of the Geolocation API. It optimistically presumes they did until they either explicitly deny it or userDecisionTimeout (if set) has elapsed and they haven't allowed it yet.
   */
  isGeolocationEnabled: boolean;
  /**
   * The Geolocation API's PositionError object resulting from an error occurring in the API call.
   */
  positionError: GeolocationPositionError | undefined;
  /**
   * The Geolocation API's timestamp value representing the time at which the location was retrieved.
   */
  timestamp: EpochTimeStamp | undefined;
}

/**
 * Hook abstracting away the interaction with the Geolocation API.
 * @param config - the configuration to use
 */
export const useAsyncGeolocation = (
  config: GeolocatedConfig = {},
): GeolocatedResult => {
  const {
    positionOptions = {
      enableHighAccuracy: true,
      maximumAge: Duration.fromObject({ seconds: 15 }).as('milliseconds'),
      timeout: Duration.fromObject({ seconds: 60 }).as('milliseconds'),
    },
    isOptimisticGeolocationEnabled = true,
    userDecisionTimeout = undefined,
    geolocationProvider = typeof navigator !== 'undefined'
      ? navigator.geolocation
      : undefined,
    watchLocationPermissionChange = true,
    onError,
    onSuccess,
  } = config;

  const userDecisionTimeoutId = useRef(0);
  const isCurrentlyMounted = useRef(true);

  const [isGeolocationEnabled, setIsGeolocationEnabled] = useState(
    isOptimisticGeolocationEnabled,
  );

  const [coords, setCoords] = useState<GeolocationCoordinates | undefined>();
  const [timestamp, setTimestamp] = useState<EpochTimeStamp | undefined>();
  const [positionError, setPositionError] = useState<
    GeolocationPositionError | undefined
  >();
  const [permissionState, setPermissionState] = useState<
    PermissionState | undefined
  >();

  const cancelUserDecisionTimeout = useCallback(() => {
    if (userDecisionTimeoutId.current) {
      window.clearTimeout(userDecisionTimeoutId.current);
    }
  }, []);

  const handlePositionError = useCallback(
    (error?: GeolocationPositionError) => {
      cancelUserDecisionTimeout();
      if (isCurrentlyMounted.current) {
        setCoords(() => undefined);
        setIsGeolocationEnabled(false);
        setPositionError(error);
      }
      onError?.(error);
    },
    [onError, cancelUserDecisionTimeout],
  );

  const handlePositionSuccess = useCallback(
    (position: GeolocationPosition) => {
      cancelUserDecisionTimeout();
      if (isCurrentlyMounted.current) {
        captureMessage('Geolocation received', {
          extra: { position },
          level: 'debug',
        });
        setCoords(position.coords);
        setTimestamp(position.timestamp);
        setIsGeolocationEnabled(true);
        setPositionError(() => undefined);
      }
      onSuccess?.(position);
    },
    [onSuccess, cancelUserDecisionTimeout],
  );

  const getPosition = useCallback((): Promise<GeolocationPosition> => {
    return new Promise((resolve, reject) => {
      if (!geolocationProvider?.getCurrentPosition) {
        throw new Error('The provided geolocation provider is invalid');
      }

      if (userDecisionTimeout) {
        userDecisionTimeoutId.current = window.setTimeout(() => {
          handlePositionError();
          reject();
        }, userDecisionTimeout);
      }

      geolocationProvider.getCurrentPosition(
        (position) => {
          handlePositionSuccess(position);
          resolve(position);
        },
        (err) => {
          handlePositionError(err);
          reject(err);
        },
        positionOptions,
      );
    });
  }, [
    geolocationProvider,
    userDecisionTimeout,
    handlePositionError,
    handlePositionSuccess,
    positionOptions,
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: working, don't mess with it
  useEffect(() => {
    let permission: PermissionStatus;

    if (
      watchLocationPermissionChange &&
      geolocationProvider &&
      'permissions' in navigator
    ) {
      navigator.permissions
        .query({ name: 'geolocation' })
        .then((result) => {
          permission = result;
          permission.onchange = () => {
            setPermissionState(permission.state);
          };
          return;
        })
        .catch(captureException);
    }

    return () => {
      if (permission) {
        permission.onchange = null;
      }
    };
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: working, don't mess with it
  useEffect(() => {
    return () => {
      cancelUserDecisionTimeout();
    };
  }, [permissionState]);

  return {
    coords,
    getPosition,
    isGeolocationAvailable: Boolean(geolocationProvider),
    isGeolocationEnabled,
    positionError,
    timestamp,
  };
};
